<template>
  <div class="mc-root">

    <!-- Loading -->
    <div v-if="loading" class="mc-splash">
      <div class="mc-ring"></div>
      <p>Loading your visit…</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="mc-screen">
      <div class="mc-box mc-box--error">
        <div class="mc-icon mc-icon--error">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <h2>{{ errorTitle }}</h2>
        <p>{{ error }}</p>
        <div v-if="outOfRange" style="margin-top:16px;">
          <p style="font-size:13px;color:#7a90b0;">You are {{ distanceAway }}m away. Required: within {{ requiredRadius }}m.</p>
          <button class="mc-btn mc-btn--primary" @click="tryCheckIn" style="margin-top:12px;">Try Again</button>
        </div>
      </div>
    </div>

    <!-- Success -->
    <div v-else-if="success" class="mc-screen">
      <div class="mc-box mc-box--success">
        <div class="mc-icon mc-icon--success">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <h2>Checked In!</h2>
        <p class="mc-sub">You're now checked in. Head to reception to collect your visitor badge.</p>
        <div class="mc-visit-card">
          <div class="mc-row"><span class="mc-label">Name</span><span class="mc-val">{{ visitData.visitor_name }}</span></div>
          <div class="mc-row"><span class="mc-label">Host</span><span class="mc-val">{{ visitData.host_name }}</span></div>
          <div class="mc-row"><span class="mc-label">Time</span><span class="mc-val">{{ fmtTime(visitData.checked_in_at) }}</span></div>
        </div>
      </div>
    </div>

    <!-- Ready to check in -->
    <div v-else-if="visitInfo" class="mc-content">
      <div class="mc-header">
        <div class="mc-company">{{ visitInfo.company }}</div>
        <div class="mc-title">Mobile Check-In</div>
        <div class="mc-sub">Hello {{ visitInfo.visitor.first_name }}, you're ready to check in.</div>
      </div>

      <div class="mc-visit-card mc-visit-card--info">
        <div class="mc-row"><span class="mc-label">Host</span><span class="mc-val">{{ visitInfo.visit.host_name }}</span></div>
        <div class="mc-row"><span class="mc-label">Purpose</span><span class="mc-val">{{ visitInfo.visit.purpose || '—' }}</span></div>
        <div class="mc-row"><span class="mc-label">Scheduled</span><span class="mc-val">{{ fmtDate(visitInfo.visit.scheduled_at) }}</span></div>
      </div>

      <!-- Geofence status -->
      <div v-if="geoStatus" class="mc-geo-status" :class="'mc-geo-status--' + geoStatus">
        <div v-if="geoStatus === 'checking'" class="mc-geo-inner">
          <div class="mc-ring mc-ring--sm"></div>
          <span>Detecting your location…</span>
        </div>
        <div v-else-if="geoStatus === 'in-range'" class="mc-geo-inner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
          <span>You are within range of {{ visitInfo.site?.name || 'the venue' }}</span>
        </div>
        <div v-else-if="geoStatus === 'no-geo'" class="mc-geo-inner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/></svg>
          <span>Location permission needed. Please allow access and try again.</span>
        </div>
      </div>

      <button
        class="mc-btn mc-btn--primary mc-btn--lg"
        :disabled="checkinInProgress || geoStatus === 'no-geo'"
        @click="tryCheckIn"
      >
        <div v-if="checkinInProgress" class="mc-ring mc-ring--sm mc-ring--white"></div>
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        {{ checkinInProgress ? 'Checking In…' : 'Check In Now' }}
      </button>

      <p class="mc-tip">Your location will be used to confirm you are on-site. It is not stored.</p>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'VisitorMobileCheckIn',
  data() {
    return {
      loading: true, error: null, errorTitle: 'Error',
      success: false, visitInfo: null, visitData: null,
      companyCode: '', businessId: '', token: '',
      geoStatus: null,
      checkinInProgress: false,
      outOfRange: false, distanceAway: 0, requiredRadius: 200,
    }
  },
  created() {
    this.companyCode = this.$route.params.companyCode
    this.businessId  = this.$route.params.businessId
    this.token       = this.$route.params.token
    this.loadVisit()
  },
  methods: {
    async loadVisit() {
      this.loading = true
      try {
        const r = await axios.get(
          `public/visitors/${this.companyCode}/${this.businessId}/pre-arrival/${this.token}`
        )
        this.visitInfo = r.data.data
        // Pre-request geolocation permission so button works immediately
        this.getLocation()
      } catch (e) {
        this.errorTitle = 'Visit Not Found'
        this.error = e.response?.data?.message ?? 'This mobile check-in link is invalid or has already been used.'
      } finally {
        this.loading = false
      }
    },
    getLocation() {
      if (!navigator.geolocation) { this.geoStatus = 'no-geo'; return }
      this.geoStatus = 'checking'
      navigator.geolocation.getCurrentPosition(
        () => { this.geoStatus = 'in-range' }, // optimistic — actual check is server-side
        () => { this.geoStatus = 'no-geo' },
        { timeout: 10000, maximumAge: 30000 }
      )
    },
    async tryCheckIn() {
      this.outOfRange       = false
      this.error            = null
      this.checkinInProgress = true

      const doCheckIn = async (lat, lng) => {
        try {
          await axios.post(
            `public/visitors/${this.companyCode}/${this.businessId}/mobile-checkin/${this.token}`,
            { lat, lng }
          )
          this.success   = true
          this.visitData = {
            visitor_name: `${this.visitInfo.visitor.first_name} ${this.visitInfo.visitor.last_name}`,
            host_name:    this.visitInfo.visit.host_name,
            checked_in_at: new Date().toISOString(),
          }
        } catch (e) {
          const data = e.response?.data
          if (data?.status === 'out_of_range') {
            this.outOfRange     = true
            this.errorTitle     = 'Too Far Away'
            this.error          = data.message
            this.distanceAway   = data.distance_meters
            this.requiredRadius = data.required_meters
          } else {
            this.errorTitle = 'Check-In Failed'
            this.error      = data?.message ?? 'Could not complete check-in. Please try again or check in at reception.'
          }
        } finally {
          this.checkinInProgress = false
        }
      }

      if (!navigator.geolocation) {
        await doCheckIn(null, null)
        return
      }

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          await doCheckIn(pos.coords.latitude, pos.coords.longitude)
        },
        async () => {
          // No location → let server decide (will skip geofence if no coords on site)
          await doCheckIn(null, null)
        },
        { timeout: 10000, maximumAge: 0 }
      )
    },
    fmtDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleString('en-GB', {
        weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      })
    },
    fmtTime(d) {
      if (!d) return '—'
      return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    },
  },
}
</script>

<style scoped>
* { box-sizing: border-box; }
.mc-root { min-height: 100vh; background: #f0f4f8; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; display: flex; flex-direction: column; }

.mc-splash { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; gap: 14px; color: #7a90b0; font-size: 14px; }
.mc-ring { width: 36px; height: 36px; border: 3px solid #e4eaf2; border-top-color: #004AAD; border-radius: 50%; animation: spin .8s linear infinite; flex-shrink: 0; }
.mc-ring--sm { width: 18px; height: 18px; border-width: 2px; }
.mc-ring--white { border-color: rgba(255,255,255,.3); border-top-color: #fff; }
@keyframes spin { to { transform: rotate(360deg); } }

.mc-screen { display: flex; align-items: center; justify-content: center; flex: 1; padding: 24px; }
.mc-box { background: #fff; border-radius: 16px; padding: 36px 28px; max-width: 420px; width: 100%; text-align: center; box-shadow: 0 4px 24px rgba(0,0,0,.08); }
.mc-icon { width: 56px; height: 56px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; }
.mc-icon--error   { background: #fef2f2; color: #dc2626; }
.mc-icon--success { background: #004AAD; }
.mc-box h2 { font-size: 22px; font-weight: 800; color: #1a2b45; margin: 0 0 10px; }

.mc-content { max-width: 460px; margin: 0 auto; padding: 32px 20px 48px; width: 100%; }
.mc-header { text-align: center; margin-bottom: 24px; }
.mc-company { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #004AAD; margin-bottom: 8px; }
.mc-title { font-size: 24px; font-weight: 800; color: #1a2b45; margin-bottom: 6px; }
.mc-sub { font-size: 14px; color: #4a5e78; line-height: 1.5; }

.mc-visit-card { background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; overflow: hidden; margin-bottom: 20px; }
.mc-visit-card--info { margin-bottom: 20px; }
.mc-row { display: flex; padding: 10px 16px; border-bottom: 1px solid #f0f4f8; }
.mc-row:last-child { border-bottom: none; }
.mc-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #aab8cc; width: 90px; flex-shrink: 0; }
.mc-val { font-size: 13px; font-weight: 600; color: #1a2b45; }

.mc-geo-status { border-radius: 10px; padding: 12px 16px; margin-bottom: 16px; }
.mc-geo-status--checking { background: #eff6ff; color: #2563eb; }
.mc-geo-status--in-range  { background: #f0fdf4; color: #15803d; }
.mc-geo-status--no-geo    { background: #fff7ed; color: #c2410c; }
.mc-geo-inner { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; }

.mc-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-size: 14px; font-weight: 700; border-radius: 10px; border: none; cursor: pointer; font-family: inherit; transition: background .15s; }
.mc-btn--primary { background: #004AAD; color: #fff; padding: 0 24px; height: 48px; }
.mc-btn--primary:hover:not(:disabled) { background: #003b8e; }
.mc-btn--primary:disabled { opacity: .5; cursor: not-allowed; }
.mc-btn--lg { width: 100%; font-size: 16px; height: 54px; }
.mc-tip { font-size: 12px; color: #aab8cc; text-align: center; margin-top: 14px; line-height: 1.5; }
</style>
